'use client'

import { createContext, type ReactNode, useContext, useMemo } from 'react'

/**
 * `useRouteLoaderData(routeId)` — reads per-segment data from context.
 *
 * The contextual guards resolve the segment's data before rendering and publish
 * it through this context via the corresponding `app/**` layout/boundary;
 * consumers (e.g. `CommunityDashboardView`, `NetworkShowView`) read it by id.
 */
type LoaderDataMap = Record<string, unknown>

const RouteLoaderDataContext = createContext<LoaderDataMap>({})

export const RouteLoaderDataProvider = ({
  id,
  data,
  children,
}: {
  id: string
  data: unknown
  children: ReactNode
}) => {
  const parent = useContext(RouteLoaderDataContext)
  const value = useMemo(() => ({ ...parent, [id]: data }), [parent, id, data])
  return (
    <RouteLoaderDataContext.Provider value={value}>
      {children}
    </RouteLoaderDataContext.Provider>
  )
}

export function useRouteLoaderData<T = unknown>(routeId: string): T {
  return useContext(RouteLoaderDataContext)[routeId] as T
}
